<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TeamNumbersAPI from 'dashboard/api/aceleres/teamNumbers';

const props = defineProps({ contact: { type: Object, required: true } });
const emit = defineEmits(['close']);
const events = ref([]);
const isLoading = ref(false);
const titles = {
  team_number_marked: 'Entrou na equipe',
  team_number_unmarked: 'Saiu da equipe',
  team_number_manual_override_set: 'Passou a valer a decisão manual',
  team_number_manual_override_cleared: 'Decisão devolvida ao sistema',
  team_number_auto_blocked: 'Decisão automática bloqueada',
  contact_created: 'Contato criado',
  contact_updated: 'Contato atualizado',
  contact_owner_changed: 'Responsável alterado',
  personal_instance_conversation_opened:
    'Conversa aberta pela instância pessoal',
  personal_instance_lead_blocked: 'Uso da instância pessoal bloqueado',
  personal_instance_authorization_granted:
    'Uso da instância pessoal autorizado',
};
const shownEvents = computed(() =>
  events.value.filter(event => titles[event.action])
);
const formatDate = value =>
  value
    ? messageTimestamp(
        new Date(value).getTime() / 1000,
        'dd/MM/yyyy HH:mm'
      ).replace(' ', ' · ')
    : '—';
const title = event => titles[event.action];
const description = event => {
  const automatic = event.source === 'classification_sync';
  const actor = event.user?.name || 'O sistema';
  if (event.action === 'team_number_marked')
    return automatic
      ? 'Pelo cadastro do Acelerity (automático)'
      : `${actor} marcou manualmente`;
  if (event.action === 'team_number_unmarked')
    return automatic
      ? 'Pelo cadastro do Acelerity (automático)'
      : `${actor} removeu`;
  if (event.action === 'team_number_manual_override_set')
    return 'O cadastro do Acelerity deixou de decidir';
  if (event.action === 'team_number_manual_override_cleared')
    return 'O cadastro do Acelerity voltou a decidir';
  if (event.action === 'contact_owner_changed')
    return `${event.previous_value || 'Sem responsável'} → ${event.new_value || 'Sem responsável'}`;
  if (event.action !== 'contact_updated') return '';
  const attribute = event.metadata?.attribute;
  if (attribute === 'identifier')
    return 'Identificador do Instagram adicionado';
  if (attribute === 'phone_number') return 'Telefone atualizado';
  if (attribute === 'email') return 'E-mail atualizado';
  if (attribute === 'name') return 'Nome atualizado';
  return 'Dados do contato atualizados';
};
const source = event => {
  if (event.source === 'contact_update') return 'pela página do contato';
  if (event.source === 'classification_sync') return 'sincronização automática';
  if (event.source === 'contact')
    return event.user?.name
      ? `por ${event.user.name}`
      : 'pela página do contato';
  if (event.user?.name) return `por ${event.user.name}`;
  return 'sistema';
};
const dotClass = event => {
  if (event.action === 'team_number_marked')
    return event.source === 'classification_sync' ? 'auto' : 'enter';
  if (event.action === 'team_number_unmarked') return 'leave';
  if (event.action === 'team_number_manual_override_set') return 'manual';
  if (
    event.action.includes('auto') ||
    event.action === 'team_number_manual_override_cleared'
  )
    return 'auto';
  return 'other';
};
const fetchHistory = async () => {
  isLoading.value = true;
  try {
    const { data } = await TeamNumbersAPI.history(props.contact.id);
    events.value = data.payload || [];
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        'Não foi possível carregar o histórico do contato.'
    );
  } finally {
    isLoading.value = false;
  }
};
const handleKeydown = event => {
  if (event.key === 'Escape') emit('close');
};
onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
  fetchHistory();
});
onBeforeUnmount(() => document.removeEventListener('keydown', handleKeydown));
</script>

<template>
  <Teleport to="body">
    <div
      class="team-number-history__backdrop fixed inset-0 z-[2147483647] grid place-items-center overflow-y-auto bg-[rgba(20,24,31,0.35)] p-4"
      @click.self="emit('close')"
    >
      <section
        class="team-number-history"
        role="dialog"
        aria-modal="true"
        aria-label="Histórico do número da equipe"
      >
        <header>
          <span class="team-number-history__avatar">{{
            (contact.name || 'C').slice(0, 2).toUpperCase()
          }}</span>
          <div>
            <h2>Histórico</h2>
            <p>{{ contact.name || 'Sem nome' }}</p>
          </div>
          <button
            class="!rounded-full"
            aria-label="Fechar"
            @click="emit('close')"
          >
            <Icon icon="i-lucide-x" class="size-4" />
          </button>
        </header>
        <div
          class="team-number-history__body [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar-track]:bg-transparent"
        >
          <p v-if="isLoading">Carregando...</p>
          <p v-else-if="!shownEvents.length">
            Nenhuma movimentação registrada.
          </p>
          <ol v-else>
            <li v-for="event in shownEvents" :key="event.id">
              <span class="team-number-history__rail"
                ><i :class="dotClass(event)"
              /></span>
              <div>
                <strong>{{ title(event) }}</strong>
                <p v-if="description(event)">{{ description(event) }}</p>
                <small
                  >{{ formatDate(event.created_at) }} ·
                  {{ source(event) }}</small
                >
              </div>
            </li>
          </ol>
        </div>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.team-number-history {
  width: min(560px, 100%);
  max-height: calc(100dvh - 32px);
  font-family: 'Plus Jakarta Sans', sans-serif;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 24px 60px #0003;
  color: #1f2328;
}
.team-number-history header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f1f3;
}
.team-number-history__avatar {
  width: 40px;
  height: 40px;
  flex: none;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #e6eefc;
  color: #1d4f91;
  font-size: 13px;
  font-weight: 600;
}
.team-number-history header div {
  flex: 1;
  min-width: 0;
}
.team-number-history h2 {
  margin: 0;
  line-height: 22px;
  font-size: 17px;
  font-weight: 700;
}
.team-number-history header p {
  line-height: 18px;
  margin: 2px 0 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #6b7280;
  font-size: 13px;
}
.team-number-history header button {
  padding: 0;
  border: 0;
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border-radius: 10px;
  background: #f5f5f3;
}
.team-number-history__body {
  overflow: auto;
  padding: 20px 24px 8px;
}
.team-number-history__body > p {
  color: #6b7280;
  font-size: 13px;
}
.team-number-history ol {
  margin: 0;
  padding: 0;
  list-style: none;
}
.team-number-history li {
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr);
  gap: 12px;
}
.team-number-history li > div {
  padding-bottom: 9px;
  line-height: 17px;
}
.team-number-history__rail {
  position: relative;
  display: flex;
  justify-content: center;
}
.team-number-history li:not(:last-child) .team-number-history__rail:after {
  content: '';
  position: absolute;
  top: 12px;
  bottom: 0;
  width: 2px;
  background: #f0f1f3;
}
.team-number-history__rail i {
  width: 10px;
  height: 10px;
  margin-top: 4px;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 0 1px #eceef1;
}
.team-number-history__rail .enter {
  background: #16a34a;
}
.team-number-history__rail .leave {
  background: #b42318;
}
.team-number-history__rail .manual {
  background: #9a4b00;
}
.team-number-history__rail .auto {
  background: #1d4f91;
}
.team-number-history__rail .other {
  background: #8c959f;
}
.team-number-history li strong {
  display: block;
  line-height: 18px;
  font-weight: 700;
  font-size: 14px;
}
.team-number-history li p {
  margin: 4px 0 3px;
  line-height: 17px;
  color: #444c56;
  font-size: 13px;
}
.team-number-history li small {
  color: #8c959f;
  display: block;
  line-height: 16px;
  font-size: 12px;
}
</style>
